/** @jsxImportSource @emotion/react */

export type SubmissionMethodControlProps = {
  method: 'ELECTRONIC' | 'PAPER'
  // The claim to this payer has gone out, so how it went can no longer change.
  locked: boolean
  // False for someone who cannot edit the charge: the method is still shown.
  canSwitch: boolean
  // Why the switch is unavailable for now (unsaved edits, no plan balance).
  disabledReason?: string
  onSwitch: () => void
}

const BADGE_CSS = {
  borderRadius: 4,
  padding: '1px 6px',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.3,
  textTransform: 'uppercase' as const,
}

// Paper is the exception and means work by hand, so it stands out.
const METHOD_COLORS = {
  ELECTRONIC: { background: '#e7f1fb', color: '#1f5f99' },
  PAPER: { background: '#fff3cd', color: '#856404' },
}

// How the claim to one payer on the charge is sent, beside that payer's plan.
export const SubmissionMethodControl = ({
  method,
  locked,
  canSwitch,
  disabledReason,
  onSwitch,
}: SubmissionMethodControlProps) => {
  const other = method === 'PAPER' ? 'Electronic' : 'Paper'

  return (
    <span css={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
      <span css={{ ...BADGE_CSS, ...METHOD_COLORS[method] }}>
        {method === 'PAPER' ? 'Paper' : 'Electronic'}
      </span>
      {locked && (
        <i
          className="fas fa-lock text-muted"
          title="This claim has already been submitted, so how it is sent can no longer change."
        />
      )}
      {!locked && canSwitch && (
        // The reason sits on the wrapper: a disabled button shows no tooltip.
        <span title={disabledReason}>
          <button
            type="button"
            className="btn btn-link p-0"
            css={{ fontSize: 12, lineHeight: 1, ...(disabledReason ? { pointerEvents: 'none' } : {}) }}
            disabled={!!disabledReason}
            onClick={onSwitch}>
            Switch to {other}
          </button>
        </span>
      )}
    </span>
  )
}
